import { go, goReplace, hashQuery } from '../../App';
import { useLoad, usePolled } from '../../data';
import { MonthCalendar, PageHead, monthWindow } from '../../ui';
import { Account, Booking } from '../../types';

// Calendar (canon 09 supplier "Calendar" · Register template, Full grid). The
// month ahead for a supplier: the jobs they've said yes to and the days they've
// blocked, at a glance — the "what's my month" the schedule list (Confirmed)
// can't show. No new endpoint: it windows GET /v1/bookings by the byStart GSI
// (from/to) exactly as the hotel Calendar does, and reads GET /v1/availability
// for the blocked days the supplier owns. Both are the supplier's OWN partition
// (token-pinned), so a hotel's bookings never appear here (DECISION-020). A job
// chip opens the EXISTING Booking-detail Record (a full-page nav — changing
// screen, canon-correct); a blocked day opens the availability screen. The grid
// is the shared MonthCalendar (canon 04, built once) — same grid as the hotel's.

export function Calendar() {
  const account = useLoad<Account>('/account');
  const tz = account.data?.timezone ?? 'Europe/Athens';
  // "This month" is the supplier's month, not the device's.
  const thisMonth = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit' })
    .format(new Date())
    .slice(0, 7);
  const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());

  const q = hashQuery().get('m');
  const m = q && /^\d{4}-\d{2}$/.test(q) ? q : thisMonth;

  // Fetch exactly the six-week window the grid will show.
  const { from, to } = monthWindow(m);
  const { data, error } = usePolled<{ items: Booking[] }>(`/bookings?from=${from}&to=${to}`);
  const items = data ? data.items : null;
  // The blocked days the supplier owns, over the same window (canon 11 F1). The
  // block carries a private reason (Eva relays it); the grid needs only the
  // date, so it reads the same {blocks:[{date}]} shape the Availability screen does.
  const blocks = useLoad<{ blocks: { date: string }[] }>(`/availability?from=${from}&to=${to}`);
  const blocked = new Set((blocks.data?.blocks ?? []).map((b) => b.date));

  const setMonth = (next: string) => goReplace(`/calendar?m=${next}`);

  // Confirmed work drills to the Confirmed drawer; anything else (completed,
  // cancelled) to Booking history — both open the same shared Record.
  const openBooking = (b: Booking) => go(b.status === 'confirmed' ? `/confirmed/${b.id}` : `/history/${b.id}`);

  return (
    <div>
      <PageHead title="Calendar" sub="Your month — the jobs you've said yes to, and the days you've blocked." />

      {error && !items && <p className="error-line">{error}</p>}

      <MonthCalendar
        month={m}
        thisMonth={thisMonth}
        todayStr={todayStr}
        bookings={items}
        blocked={blocked}
        onMonth={setMonth}
        onOpenBooking={openBooking}
        onOpenBlocked={() => go('/availability')}
        chipTitle={(b) => `${b.productName ?? b.productId} · ${b.hotelName ?? ''}`}
      />
    </div>
  );
}
